/**
 * Discharge and transfer summaries (Phase 16E, REQ-DSC-10..18, 21..23).
 *
 * The summary is drafted by the server on discharge and on a hospitalization hold. Facts come from
 * the record; the author writes the narrative and goal outcomes, signs (or sends to QA), and a
 * coordinator records where it was sent. Errors are thrown as ApiError (apiClient); show them with
 * describeApiError.
 */
import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import type {
  NoticeLetterLink,
  AddDeliveryRequest,
  AmendDischargeSummaryRequest,
  DischargeSummary,
  DischargeSummaryKind,
  DischargeSummaryListItem,
  SummaryPdfLink,
  UpdateDischargeSummaryRequest,
} from '../../dtos/patientmr/dischargeSummary.dto';

const base = (patientId: string) => `/patientmr/${encodeURIComponent(patientId)}/discharge-summaries`;
const one = (patientId: string, summaryId: string) => `${base(patientId)}/${encodeURIComponent(summaryId)}`;

function unwrap<T>(response: ApiResponse<T>, fallback: string): T {
  if (response.success && response.data) return response.data;
  throw new Error(response.error?.message || fallback);
}

export class DischargeSummaryService {
  /** Summaries of a patient, newest first (no facts or narrative) */
  static async list(patientId: string): Promise<DischargeSummaryListItem[]> {
    const response: ApiResponse<{ summaries: DischargeSummaryListItem[] }> = await apiClient.get(base(patientId));
    return unwrap(response, 'Failed to load discharge summaries').summaries ?? [];
  }

  /** Create the summary the server did not draft (e.g. a Deceased discharge) */
  static async create(patientId: string, kind: DischargeSummaryKind = 'DISCHARGE'): Promise<DischargeSummary> {
    const response: ApiResponse<DischargeSummary> = await apiClient.post(base(patientId), { kind });
    return unwrap(response, 'Failed to create the summary');
  }

  static async get(patientId: string, summaryId: string): Promise<DischargeSummary> {
    const response: ApiResponse<DischargeSummary> = await apiClient.get(one(patientId, summaryId));
    return unwrap(response, 'Failed to load the summary');
  }

  /** Save narrative and goal outcomes of a draft (author only) */
  static async update(patientId: string, summaryId: string, body: UpdateDischargeSummaryRequest): Promise<DischargeSummary> {
    const response: ApiResponse<DischargeSummary> = await apiClient.put(one(patientId, summaryId), body);
    return unwrap(response, 'Failed to save the summary');
  }

  private static async action<T>(patientId: string, summaryId: string, action: string, body: object, fallback: string): Promise<T> {
    const response: ApiResponse<T> = await apiClient.post(`${one(patientId, summaryId)}/${action}`, body);
    return unwrap(response, fallback);
  }

  /** Re-pull diagnoses, medications, goals and visits from the record (draft or in QA) */
  static refresh(patientId: string, summaryId: string): Promise<DischargeSummary> {
    return this.action(patientId, summaryId, 'refresh', {}, 'Failed to refresh the summary');
  }

  /** Sign (author), or send to QA with submitForQa */
  static sign(patientId: string, summaryId: string, submitForQa = false): Promise<DischargeSummary> {
    return this.action(patientId, summaryId, 'sign', submitForQa ? { submitForQa: true } : {}, 'Failed to sign the summary');
  }

  static review(patientId: string, summaryId: string, decision: 'APPROVE' | 'RETURN', note?: string): Promise<DischargeSummary> {
    return this.action(patientId, summaryId, 'qa', { decision, ...(note ? { note } : {}) }, 'Failed to save the QA decision');
  }

  static amend(patientId: string, summaryId: string, body: AmendDischargeSummaryRequest): Promise<DischargeSummary> {
    return this.action(patientId, summaryId, 'amend', body, 'Failed to amend the summary');
  }

  /** Transfer summary not needed (patient returned) */
  static close(patientId: string, summaryId: string, reason: string): Promise<DischargeSummary> {
    return this.action(patientId, summaryId, 'close', { reason }, 'Failed to close the summary');
  }

  /** "Mark sent": record a delivery made outside Pelora */
  static addDelivery(patientId: string, summaryId: string, body: AddDeliveryRequest): Promise<DischargeSummary> {
    return this.action(patientId, summaryId, 'deliveries', body, 'Failed to record the delivery');
  }

  /**
   * 16E.1 (DSC-20): generate the written discharge notice letter from the saved notice record. The
   * PDF is filed in the patient's documents library; returns a 5-minute download link.
   */
  static generateNoticeLetter(patientId: string): Promise<NoticeLetterLink> {
    return this.action(patientId, 'notice', 'letter', {}, 'Failed to generate the notice letter');
  }

  /** 5-minute download link for the current version (audited) */
  static async getPdfLink(patientId: string, summaryId: string): Promise<SummaryPdfLink> {
    const response: ApiResponse<SummaryPdfLink> = await apiClient.get(`${one(patientId, summaryId)}/pdf`);
    return unwrap(response, 'Failed to prepare the PDF');
  }
}
